import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Plus, Check, X, Ban, RotateCcw, Pencil } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useResourceList } from "@/hooks/useResource";
import { useOptions } from "@/hooks/useOptions";
import { PageHeader } from "@/components/kit/PageHeader";
import { DataTablePro } from "@/components/kit/DataTablePro";
import { StatusBadge } from "@/components/kit/StatusBadge";
import { Button } from "@/components/ui/button";
import { fmtDate, shortId } from "@/lib/format";
import { useTenantColumn } from "@/hooks/useTenantColumn";
import { useTenants } from "@/hooks/useTenants";
import { WalletDrawer } from "@/drawers/WalletDrawer";

const asOpts = (arr) => (arr || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o));

export default function Wallets() {
  const { user } = useAuth();
  const opts = useOptions();
  const qc = useQueryClient();
  const [drawer, setDrawer] = useState({ open: false, item: null });
  const { data, isLoading } = useResourceList("wallets", {});
  const tcol = useTenantColumn("finance");
  const { data: tenantsData } = useTenants();

  const setStatus = async (id, status) => {
    try {
      await http.post(`/wallets/${id}/status`, { status });
      qc.invalidateQueries({ queryKey: ["wallets"] });
      toast.success(`Wallet ${status === "active" ? "approved" : status}`);
    } catch (e) {
      toast.error("Update failed", { description: e?.response?.data?.detail || "Try again." });
    }
  };

  const columns = [
    { key: "id", label: "ID", mono: true, render: (r) => <span className="text-muted-foreground">{shortId(r.id)}</span> },
    { key: "user_name", label: "User", sortable: true, render: (r) => (
      <div className="min-w-0"><div className="truncate font-medium text-foreground">{r.user_name}</div><div className="truncate text-xs text-muted-foreground">{r.user_email}</div></div>
    ) },
    { key: "label", label: "Label", render: (r) => r.label || "—" },
    { key: "method", label: "Method", render: (r) => r.method || "—" },
    { key: "network", label: "Network", render: (r) => <span className="text-muted-foreground">{r.network || "—"}</span> },
    { key: "currency", label: "Currency" },
    { key: "address", label: "Address", mono: true, render: (r) => <span className="text-muted-foreground break-all">{r.address}</span> },
    { key: "status", label: "Status", sortable: true, render: (r) => <StatusBadge status={r.status || "active"} /> },
    { key: "created_at", label: "Created", sortable: true, render: (r) => fmtDate(r.created_at) },
  ];

  const filters = [
    ...(tcol.show ? [{ key: "tenant_id", label: "Tenant", getValue: (r) => r.tenant_id, options: (tenantsData?.items || []).map((t) => ({ value: t.internalId, label: t.name })) }] : []),
    { key: "status", label: "Status", getValue: (r) => r.status || "active", options: asOpts(["pending", "active", "disabled", "rejected"]) },
    { key: "method", label: "Method", getValue: (r) => r.method, options: asOpts((opts.payment_methods || []).map((m) => m.name)) },
    { key: "currency", label: "Currency", getValue: (r) => r.currency, options: asOpts(["USDT", "USD", "EUR", "BTC"]) },
  ];

  const rowActions = (row) => {
    const st = row.status || "active";
    const acts = [];
    if (st !== "active") acts.push({ label: "Approve", icon: Check, onClick: () => setStatus(row.id, "active"), testId: `wallet-approve-${row.id}` });
    if (st === "pending") acts.push({ label: "Reject", icon: X, danger: true, onClick: () => setStatus(row.id, "rejected"), testId: `wallet-reject-${row.id}` });
    if (st === "active") acts.push({ label: "Disable", icon: Ban, danger: true, onClick: () => setStatus(row.id, "disabled"), testId: `wallet-disable-${row.id}` });
    if (st === "disabled") acts.push({ label: "Reactivate", icon: RotateCcw, onClick: () => setStatus(row.id, "active"), testId: `wallet-reactivate-${row.id}` });
    acts.push({ label: "Edit", icon: Pencil, onClick: () => setDrawer({ open: true, item: row }), testId: `wallet-edit-${row.id}` });
    return acts;
  };

  return (
    <div className="space-y-5">
      <PageHeader title="Wallets" subtitle="Moderate every user's payout wallet — approve, reject, disable or add wallets on their behalf." />
      <DataTablePro testId="all-wallets-table" columns={tcol.inject(columns, 1)} rows={data?.items || []} loading={isLoading}
        filters={filters} searchKeys={["user_name", "user_email", "address", "label", "method"]} searchPlaceholder="Search by user, email, address, method..."
        exportName="wallets" rowActions={rowActions} emptyText="No wallets yet."
        toolbarRight={(
          <Button onClick={() => setDrawer({ open: true, item: null })} data-testid="admin-add-wallet-btn" className="h-9 gap-1.5 bg-white text-black hover:bg-white/90"><Plus className="h-4 w-4" /> Add Wallet</Button>
        )} />
      <WalletDrawer open={drawer.open} item={drawer.item} admin onOpenChange={(o) => setDrawer((d) => ({ ...d, open: o }))} />
    </div>
  );
}
